import { ReactElement, Fragment } from "react";
import AxisHorizontal from "../../../../../components/charts/AxisHorizontal";
import { getTicks, useChartDimensions, useUniqueId } from "../../../../../components/charts/chartUtils";
import AxisVertical from "../../../../../components/charts/AxisVertical";
import Chart from "../../../../../components/charts/chart";
import { IPowerOnOffReport } from "../../types";
import * as d3 from "d3";
import { getPalette } from "../../../../../configuration/configuration";
import { formatNumber } from "../../../../../system/utils";
import HtmlTooltip from "../../../../../components/charts/tooltip";

interface IAvgPowerOnOffChartProps {
  data: IPowerOnOffReport[];
}

const paddingInner = 0.1;
const paddingOuter = 0.1;
const align = 0.5;

const AvgPowerOnOffChart = ({ data }: IAvgPowerOnOffChartProps): ReactElement => {
  const [ref, dimensions] = useChartDimensions<HTMLDivElement>({
    height: 200,
    marginBottom: 20,
    marginLeft: 60,
    marginRight: 60,
    marginTop: 40
  });

  const chartID = useUniqueId("power-on-off-chart");

  const xAccessor = (d: IPowerOnOffReport) => d.factoryDay;
  const yAccessorOn = (d: IPowerOnOffReport) => d.avg_powerON;
  const yAccessorOff = (d: IPowerOnOffReport) => d.avg_powerOFF;
  const yAccessorBillet = (d: IPowerOnOffReport) => d.billetTons;

  const xScale = d3.scaleBand()
    .domain(data.map(x => `${x.factoryDay}`))
    .range([0, dimensions.boundedWidth])
    .paddingInner(paddingInner)
    .paddingOuter(paddingOuter)
    .align(align);

  let maximum = 200;
  let maximumTons = 0;
  data.forEach(x => maximum = Math.max(maximum, x.avg_powerON + x.avg_powerOFF));
  data.forEach(x => maximumTons = Math.max(maximumTons, x.billetTons));

  const yScale = d3.scaleLinear()
    .domain([0, maximum])
    .range([dimensions.boundedHeight, 0]);

  const yScaleBilletTons = d3.scaleLinear()
    .domain([0, maximumTons])
    .range([dimensions.boundedHeight, 0]);

  const xAccessorScaled = (d: IPowerOnOffReport) => xScale(`${xAccessor(d)}`) ?? 0;
  const yAccessorScaled = (d: IPowerOnOffReport) => yScaleBilletTons(yAccessorBillet(d));

  const lineGenerator = d3.line<IPowerOnOffReport>()
    .x(xAccessorScaled)
    .y(yAccessorScaled)
    .curve(d3.curveLinear);

  const lineOffset = xScale.bandwidth() / 2.0;

  return(
    <div ref={ref} style={{ justifySelf: "stretch", alignSelf: "stretch" }}>
      <Chart dimensions={dimensions} >
        <AxisHorizontal
          chartID={chartID}
          dimensions={dimensions}
          scale={xScale}
          label="Day"
          ticks={data.map(item => `${item.factoryDay}`)}
          formatTick={ (_value, index) => `${data[index].day}`}
        />

        <AxisVertical
          chartID={`${chartID}-avg-on-off`}
          dimensions={dimensions}
          scale={yScale}
          label="Avg Power On and Power Off"
          ticks={getTicks(yScale.domain(), 5)}
          side="LEFT"
          formatTick={(value) => (value as number).toFixed(0)}/>

        <AxisVertical
          chartID={`${chartID}-billets`}
          dimensions={dimensions}
          scale={yScaleBilletTons}
          label="Billet Tons"
          ticks={getTicks(yScaleBilletTons.domain(), 3)}
          side="RIGHT"
          formatTick={(value) => (value as number).toFixed(0)}/>

        <g>
          <circle r={5} fill={getPalette().statusColor.good.background} cy={-15} cx={6} strokeWidth={0}/>
          <text y={-10} x={15} >Average Power On</text>

          <circle r={5} fill={getPalette().statusColor.error.background} cy={-15} cx={146} strokeWidth={0}/>
          <text y={-10} x={155} >Average Power Off</text>

          <circle r={5} fill={getPalette().statusColor.warning.background} cy={-15} cx={286} strokeWidth={0}/>
          <text y={-10} x={295} >Billet Tons</text>
        </g>

        <g>
          {data && data.map((item, index) => {
            const y = yScale(yAccessorOn(item)) ?? 0;
            const yLine = yScaleBilletTons(yAccessorBillet(item)) ?? 0;
            const x = xScale(`${xAccessor(item)}`) ?? 0;
            const width = xScale.bandwidth();
            const height = dimensions.boundedHeight - y;

            const yOff = yScale(yAccessorOff(item)) ?? 0;
            const heightOff = dimensions.boundedHeight - yOff;

            return (
              <Fragment key={`${chartID}-fragment-${index}`}>
                <HtmlTooltip title={
                  <div>
                    <div className="display-flex-row align-items-center" >
                      <div style={{ background: getPalette().statusColor.good.background, width: 10, height: 10, marginRight: 10 }} />
                      <strong className="flex-grow-1">Average Power On</strong>
                      <strong className="flex-grow-0">{formatNumber(yAccessorOn(item), 1)}</strong>
                    </div>
                  </div>
                }>
                  <rect
                    className="Bars__rect"
                    fill={getPalette().statusColor.good.background}
                    height={height}
                    width={width}
                    y={y}
                    x={x}
                  />
                </HtmlTooltip>
                <HtmlTooltip title={
                  <div>
                    <div className="display-flex-row align-items-center" >
                      <div style={{ background: getPalette().statusColor.error.background, width: 10, height: 10, marginRight: 10 }} />
                      <strong className="flex-grow-1">Average Power Off</strong>
                      <strong className="flex-grow-0">{formatNumber(yAccessorOff(item), 1)}</strong>
                    </div>
                  </div>
                }>
                  <rect
                    className="Bars__rect"
                    fill={getPalette().statusColor.error.background}
                    height={heightOff}
                    width={width}
                    y={yOff - height}
                    x={x}
                  />
                </HtmlTooltip>

                <HtmlTooltip title={
                  <div>
                    <div className="display-flex-row align-items-center" >
                      <div style={{ background: getPalette().statusColor.warning.background, width: 10, height: 10, marginRight: 10 }} />
                      <strong className="flex-grow-1">Billet Tons</strong>
                      <strong className="flex-grow-0">{formatNumber(yAccessorBillet(item), 1)}</strong>
                    </div>
                  </div>}>
                  <circle
                    className="Bars__rect"
                    fill={getPalette().statusColor.warning.background}
                    r={4}
                    cy={yLine}
                    cx={x + lineOffset}
                  />
                </HtmlTooltip>
              </Fragment>
            );
          })
          }
        </g>

        <path
          transform={`translate( ${lineOffset}, 0)`}
          className={"Line"}
          fill="none"
          stroke={getPalette().statusColor.warning.background}
          strokeWidth={3}
          d={lineGenerator(data) ?? ""}
        />

      </Chart>
    </div>
  );
};

export default AvgPowerOnOffChart;
